// Total Cost of Ownership widget — plugs into Landing to demolish competitors' monthly-price illusion
import React, { useMemo, useState } from "react";
import { Sparkles, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";

// Approx GBP costs (2-yr) synthesized from AppleCare+ / Samsung Care+ public pricing
const COMPETITORS = {
  applecare: {
    label: "AppleCare+",
    monthly: 11.49,
    screen: 29,
    damage: 99,
  },
  samsungcare: {
    label: "Samsung Care+",
    monthly: 13.00,
    screen: 29,
    damage: 99,
  },
};

const TS_MONTHLY = 7.99;      // Hunter
const TS_EXCESS = 0;          // Hunter = zero excess on standard repairs

export default function TCOCalculator() {
  const nav = useNavigate();
  const [screenClaims, setScreenClaims] = useState(2);
  const [damageClaims, setDamageClaims] = useState(1);
  const [months, setMonths] = useState(24);

  const rows = useMemo(() => Object.entries(COMPETITORS).map(([key, c]) => {
    const sub = c.monthly * months;
    const excess = c.screen * screenClaims + c.damage * damageClaims;
    const total = sub + excess;
    return { key, label: c.label, sub, excess, total };
  }), [months, screenClaims, damageClaims]);

  const tsTotal = TS_MONTHLY * months + TS_EXCESS * (screenClaims + damageClaims);
  const worstComp = Math.max(...rows.map(r => r.total));
  const saving = Math.max(0, worstComp - tsTotal);

  return (
    <div
      data-testid="tco-widget"
      className="bg-card border border-border rounded-3xl p-8 lg:p-10 shadow-teal-glow relative overflow-hidden"
    >
      <div className="absolute -top-24 -left-24 w-64 h-64 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
      <div className="relative">
        <div className="overline text-secondary flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> Total cost calculator
        </div>
        <h3 className="mt-2 font-display text-3xl md:text-4xl font-light tracking-tight">
          Their monthly price hides <span className="italic text-primary">the excess.</span>
        </h3>
        <p className="mt-2 text-sm text-muted-foreground max-w-xl">
          Drag the sliders to model your usage. See the real 2-year cost — including every deductible.
        </p>

        {/* Sliders */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <div className="flex items-baseline justify-between">
              <label className="text-xs text-muted-foreground overline">Coverage period</label>
              <span className="text-sm text-primary font-semibold" data-testid="tco-months">{months} mo</span>
            </div>
            <Slider min={12} max={36} step={1} value={[months]} onValueChange={(v) => setMonths(v[0])} className="mt-3" />
          </div>
          <div>
            <div className="flex items-baseline justify-between">
              <label className="text-xs text-muted-foreground overline">Screen repairs</label>
              <span className="text-sm text-primary font-semibold" data-testid="tco-screens">{screenClaims}</span>
            </div>
            <Slider min={0} max={5} step={1} value={[screenClaims]} onValueChange={(v) => setScreenClaims(v[0])} className="mt-3" />
          </div>
          <div>
            <div className="flex items-baseline justify-between">
              <label className="text-xs text-muted-foreground overline">Other damage claims</label>
              <span className="text-sm text-primary font-semibold" data-testid="tco-damages">{damageClaims}</span>
            </div>
            <Slider min={0} max={3} step={1} value={[damageClaims]} onValueChange={(v) => setDamageClaims(v[0])} className="mt-3" />
          </div>
        </div>

        {/* Result cards */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="rounded-2xl border-2 border-primary bg-primary/5 p-6 relative" data-testid="tco-treasurescare">
            <div className="absolute -top-3 left-4 bg-primary text-primary-foreground text-[10px] overline px-2.5 py-0.5 rounded-full font-bold flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> You pay
            </div>
            <div className="overline text-primary mt-2">Treasurescare Hunter</div>
            <div className="mt-2 font-display text-5xl font-semibold text-primary" data-testid="tco-ts-total">£{tsTotal.toFixed(0)}</div>
            <div className="mt-2 text-xs text-muted-foreground">
              £{(TS_MONTHLY * months).toFixed(0)} plan + <strong className="text-primary">£0 excess</strong>
            </div>
          </div>
          {rows.map((r) => (
            <div key={r.key} className="rounded-2xl border border-border bg-card p-6" data-testid={`tco-${r.key}`}>
              <div className="overline text-muted-foreground">{r.label}</div>
              <div className="mt-2 font-display text-5xl font-semibold text-foreground/80">£{r.total.toFixed(0)}</div>
              <div className="mt-2 text-xs text-muted-foreground">
                £{r.sub.toFixed(0)} plan + <span className="text-destructive">£{r.excess.toFixed(0)} excess</span>
              </div>
            </div>
          ))}
        </div>

        {saving > 0 && (
          <div className="mt-6 flex flex-col md:flex-row md:items-center gap-4 justify-between rounded-2xl bg-secondary/10 border border-secondary/30 p-5" data-testid="tco-saving-banner">
            <div>
              <div className="text-sm text-secondary/80">You'd save with Treasurescare</div>
              <div className="font-display text-4xl text-secondary font-semibold" data-testid="tco-saving-amount">£{saving.toFixed(0)}</div>
            </div>
            <Button
              className="bg-secondary text-teal hover:bg-secondary/90"
              onClick={() => nav("/signup")}
              data-testid="tco-cta"
            >
              Lock in these savings <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
